import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { makeSelectOptions } from '@utils/makeSelectOptions';
import { getCurrencyCodeFromCountry, getCurrencySymbolFromCountry } from '@utils/getCurrencySymbol';
import { compareObjects } from '@utils/compareObjects';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useMyQuery } from '@hooks/useMyQuery';
import { useS3ImageUploader } from '@hooks/useS3ImageUploader';
import { useEntityFormMaxHeight } from '@hooks/useEntityFormMaxHeight';
import { Button } from '@components/Core/Button';
import { ImageUploader } from '@components/Core/ImageUploader/ImageUploader';
import Popup from '@components/Core/Popup/Popup';
import { SECOND_POPUP_WIDTH } from '@components/Core/Popup/constants';
import { EntityStatusRow } from '@components/Core/EntityStatusRow';
import { Input } from '@inputs/Input/Input';
import { SelectComponent } from '@inputs/SelectComponent/SelectComponent';
import { TextAreaInput } from '@inputs/TextAreaInput';
import DatePickerRow from '@inputs/DatePickerComponents/DatePickerRow';
import { ConfirmationPopup } from '@popups/ConfirmationPopup';
import { StatusPopup } from '@popups/StatusPopup';
import { ProductsTransFields } from '@pages/Products/types';
import { productsTranslations } from '@pages/Products/translations';
import { DEFAULT_NAME_MIN_LENGTH } from '@hooks/useValidation';
import { DiscountType } from '@stores/products/types';
import { Obj } from '@/types/common';
import { productFormTranslations } from './translations';
import { IProductFormProps, IProductFormWithImageFile, ProductFormTransFields } from './types';
import { approveRequiredFields, discountTypes, MIN_VALUE } from './constants';

const ProductForm: FC<IProductFormProps> = ({ product, brandId }) => {
  const {
    alertStore: { showAlert },
    productsStore: {
      getProductsCategories,
      productsCategories,
      addProduct,
      editProduct,
      changeStatus,
    },
    mainStore: { selectedCountryCode },
    profileStore: { user },
    popupStore: { openPopup, closePopup, popupName },
  } = useStore();

  const navigate = useNavigate();
  const [_, setSearchParams] = useSearchParams();

  // use addintional flag because use same "confirmation" popup here and in parent
  const [available, setAvailable] = useState(false);

  const useFormMethods = useForm<IProductFormWithImageFile>({
    defaultValues: {
      ...product,
      brandId,
      currencyCode: product
        ? product.currencyCode
        : getCurrencyCodeFromCountry(selectedCountryCode?.countryCode),
      countryCode: product ? product.countryCode : selectedCountryCode?.countryCode,
      isNeedValidation: false,
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { isDirty },
  } = useFormMethods;

  const {} = useMyQuery({
    queryKey: ['getProductsCategories'],
    queryFn: () => getProductsCategories(),
    enabled: !productsCategories,
  });

  const { mutate: addProductMutation, isPending } = useMutation({
    mutationFn: addProduct,
    mutationKey: ['addProduct'],
    onSuccess: () => {
      showAlert('Product was added succesfully', 'success');
      navigate(`/brands/${brandId}/products`, { replace: true });
    },
  });

  const { mutate: editProductMutation, isPending: editPointPending } = useMutation({
    mutationFn: editProduct,
    mutationKey: ['editProduct'],
    onSuccess: () => {
      showAlert('Product was changed succesfully', 'success');
      navigate(`/brands/${brandId}/products`);
    },
  });

  const { mutate: changeStatusMutation, isPending: changeStatusPending } = useMutation({
    mutationFn: changeStatus,
    mutationKey: ['changeStatusProduct'],
    onSuccess: (data) => {
      showAlert('Brand status was changed succesfully', 'success');
      if (data) setSearchParams({ status: data.status });
    },
  });

  const { t } = useMyTranslations<
    ProductFormTransFields | Extract<ProductsTransFields, 'addProduct'>
  >([productFormTranslations.name, productsTranslations.name]);

  useEffect(() => {
    if (product?.adminMessage) {
      // need to set Timeout beacouse of rendering problems with title prop at MsgActionModal
      // setMsgModalText(data.adminMessage);
      setTimeout(() => {
        setAvailable(true);
        openPopup('status');
      });
    }
  }, [product?.adminMessage]);

  const formateNumberInput = (name: keyof IProductFormWithImageFile, value?: string | number) => {
    const val = value ?? getValues(name);
    if (val !== undefined && val !== null) {
      const vals = val.toString().split('.');
      if (vals[1]) setValue(name, +`${vals[0]}.${vals[1].slice(0, 2)}`, { shouldDirty: true });
      else setValue(name, Number((+vals[0]).toFixed(2)), { shouldDirty: true });
    }
  };

  const setCheckedNumberInput = useCallback(
    (name: keyof IProductFormWithImageFile, value: string | number) => {
      if (!Number.isNaN(+value)) {
        const vals = value.toString().split('.');
        if (vals[1]) setValue(name, +`${vals[0]}.${vals[1].slice(0, 2)}`);
        else setValue(name, value);
      }
      switch (name) {
        case 'price':
        case 'discountValue':
          setupDiscountPrice();
          break;
        case 'discountPrice':
          onChangeDiscountPrice();
          break;
        default:
          break;
      }
    },
    []
  );

  const setupDiscountPrice = useCallback(() => {
    const p = getValues('price');
    const d = getValues('discountValue');
    if (p !== undefined && p !== null && d !== undefined && d !== null) {
      if (getValues('discountType') === 'fixed') {
        formateNumberInput('discountPrice', p - d);
      } else {
        formateNumberInput('discountPrice', Math.round(p * (1 - d / 100) * 100) / 100 || MIN_VALUE);
      }
    }
    setTimeout(() => {
      trigger('price');
      trigger('discountPrice');
      trigger('discountValue');
    });
  }, []);

  const onChangeDiscountPrice = () => {
    const p = getValues('price');
    const dp = getValues('discountPrice');
    if (p !== undefined && dp !== undefined && dp !== null) {
      if (getValues('discountType') === 'fixed') {
        formateNumberInput('discountValue', p - dp);
      } else {
        formateNumberInput(
          'discountValue',
          Math.round(100 * (1 - dp / p) * 100) / 100 || MIN_VALUE
        );
      }
    }
    setTimeout(() => {
      trigger('discountPrice');
      trigger('discountValue');
    });
  };

  const onDiscountTypeSelect = useCallback((val: Obj | undefined | null) => {
    if (val) {
      setValue('discountType', val.value as DiscountType, { shouldDirty: true });
      setupDiscountPrice();
      setTimeout(() => {
        trigger('startDate');
        trigger('expiredDate');
      });
    } else {
      setValue('discountType', val, { shouldDirty: true });
      setValue('discountType', null);
      setValue('discountValue', null);
      setValue('discountPrice', null);
      setValue('startDate', null);
      setValue('expiredDate', null);
      setTimeout(() => {
        trigger('discountPrice');
        trigger('discountValue');
        trigger('startDate');
        trigger('expiredDate');
      });
    }
  }, []);

  const { uploadImage, isLoading: isImageUploading } =
    useS3ImageUploader<IProductFormWithImageFile>((val) => {
      if (product) editProductMutation({ ...product, ...val });
      else addProductMutation(val);
    });

  const sendRequest = useCallback(
    async (formData: IProductFormWithImageFile) => {
      const { imageUrlFile } = formData;
      if (imageUrlFile)
        await uploadImage(
          formData,
          imageUrlFile,
          'imageUrl',
          `bussiness-profile/${user?.id}/products`
        );
      else {
        if (product) editProductMutation({ ...product, ...formData });
        else addProductMutation(formData);
      }
      closePopup();
    },
    [product]
  );

  const onSubmit = useCallback(
    (formData: IProductFormWithImageFile) => {
      if (product && !compareObjects(product, { ...product, ...formData }, approveRequiredFields)) {
        sendRequest(formData);
      } else {
        setValue('isNeedValidation', true);
        setAvailable(true);
        openPopup('confirmation');
      }
    },
    [product]
  );

  const renderSubmitBtn = useCallback(
    (place: string) => (
      <Button
        text={product ? t('save') : t('addProduct', { ns: productsTranslations.name })}
        onClick={handleSubmit(onSubmit)}
        disabled={
          isImageUploading || isPending || editPointPending || changeStatusPending || !isDirty
        }
        styleProp={`max-w-[170px] sm-brand-form-submit ${
          place === 'inside' ? 'md-brand-form-submit-hide' : 'md-brand-form-submit-show'
        }`}
      />
    ),
    [isImageUploading, isPending, editPointPending, changeStatusPending, product, isDirty]
  );

  const maxHeight = useEntityFormMaxHeight();
  const CurrencyIcon = useMemo(() => {
    // return <span>{getValues('currencyCode')}</span>;
    return <span>{getCurrencySymbolFromCountry(selectedCountryCode?.countryCode)}</span>;
  }, []);

  const discountValueIcon = useMemo(() => {
    if (watch('discountType') === 'fixed') return CurrencyIcon;
    if (watch('discountType') === 'percent') return <span>%</span>;
    return null;
  }, [watch('discountType'), CurrencyIcon]);

  return (
    // use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error
    // @ts-ignore
    <>
      <FormProvider {...useFormMethods}>
        <form
          id="form"
          className="flex gap-[32px] px-[48px] pb-[48px] sm-page-padding md-brand-form-right overflow-scroll"
          style={{ maxHeight }}
        >
          <section className="section">
            <div className="bg-white flex flex-col shadow-sm p-[24px] rounded-[16px] gap-y-[24px] w-[640px] md-brand-form-inputs">
              <h6 className="subtitle !mb-0 !uppercase">{t('basic')}</h6>
              <Input
                name="name"
                type="text"
                required
                placeholder={t('name')}
                label={t('name')}
                showRequiredIcon
                minLength={DEFAULT_NAME_MIN_LENGTH}
              />
              <TextAreaInput
                name="description"
                placeholder={t('description')}
                maxLength={140}
                label={t('description')}
              />
              <Input
                name="price"
                type="number"
                required
                placeholder={t('price')}
                showRequiredIcon
                icon={CurrencyIcon}
                iconPosition="right"
                myOnChange={(val) => setCheckedNumberInput('price', val)}
                min={MIN_VALUE}
                label={t('price')}
              />
              <SelectComponent
                name="categories"
                placeholder={t('categories')}
                options={productsCategories || []}
                optionLabel="categoryName"
                optionValue="id"
                required
                isMulti
                label={t('categories')}
                showRequiredIcon
              />
              <h6 className="subtitle !mb-0 !uppercase">{t('discount')}</h6>
              <SelectComponent
                name="discountType"
                placeholder={t('discountType')}
                options={makeSelectOptions(discountTypes)}
                onSelect={onDiscountTypeSelect}
                isClearable
                isDisabled={!watch('price')}
                label={t('discountType')}
              />
              <Input
                name="discountValue"
                type="number"
                placeholder={t('discountValue')}
                showRequiredIcon
                icon={discountValueIcon}
                iconPosition="right"
                required={!!watch('discountType')}
                disabled={!watch('discountType')}
                myOnChange={(val) => setCheckedNumberInput('discountValue', val)}
                min={MIN_VALUE}
                max={
                  watch('discountType') === 'fixed' ? watch('price') - MIN_VALUE : 100 - MIN_VALUE
                }
                label={t('discountValue')}
              />
              <Input
                name="discountPrice"
                type="number"
                placeholder={t('discountPrice')}
                showRequiredIcon
                icon={CurrencyIcon}
                iconPosition="right"
                required={!!watch('discountType')}
                disabled={!watch('discountType')}
                myOnChange={(val) => setCheckedNumberInput('discountPrice', val)}
                min={MIN_VALUE}
                max={watch('price') - MIN_VALUE}
                label={t('discountPrice')}
              />
              <DatePickerRow
                startName="startDate"
                startPlaceholder={t('startDate')}
                endName="expiredDate"
                endPlaceholder={t('expiredDate')}
                requiredStart={!!watch('discountType')}
                requiredEnd={!!watch('discountType')}
                isDisabledStart={!watch('discountType')}
                isDisabledEnd={!watch('discountType')}
                noMaxDateStart
                noMaxDateEnd
                label={t('startDate')}
                endLabel={t('expiredDate')}
                showRequiredIcons
              />
              {product && (
                <EntityStatusRow
                  status={product.status}
                  onChange={(status) => changeStatusMutation({ id: product.id, status })}
                  disabled={
                    isImageUploading || isPending || editPointPending || changeStatusPending
                  }
                  onIconClick={() => {
                    setAvailable(true);
                    openPopup('status');
                  }}
                />
              )}
            </div>
            {renderSubmitBtn('inside')}
          </section>

          <section className="section md-brand-form-left sm-brand-form-left">
            <ImageUploader name="imageUrl" title={t('imageUrl')} required />
          </section>
          {renderSubmitBtn('outside')}
        </form>
      </FormProvider>
      <Popup width={SECOND_POPUP_WIDTH} available={available} onClose={() => setAvailable(false)}>
        {popupName === 'confirmation' && (
          <ConfirmationPopup
            title={t('addProduct', { ns: productsTranslations.name })}
            onAccept={() => sendRequest(getValues())}
            text={t('confirmationText')}
          />
        )}
        {popupName === 'status' && product?.adminMessage && (
          <StatusPopup status="Revise" text={product?.adminMessage} />
        )}
      </Popup>
    </>
  );
};

export default observer(ProductForm);
